import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Check, ChevronsUpDown, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { adminApi, type AdminLocation } from '@/api/admin';
import { qk } from '@/lib/query-keys';

/**
 * Platform-admin-only tenant switcher.
 *
 * Renders as a dropdown at the top of the Sidebar showing every Location
 * in the system. Clicking one calls POST /api/v1/admin/switch/:id, which
 * updates the server-side session's current_location_id. On success we
 * hard-invalidate every query so the whole UI reloads under the new
 * tenant context — no partial state carrying over.
 */
export function LocationSwitcher({
  currentLocationId,
  currentLocationName,
}: {
  currentLocationId: string | null;
  currentLocationName: string | null;
}): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Fetch always so we can resolve the current location's name even when
  // the platform admin is viewing a tenant they aren't a member of
  // (which is the common case).
  const locations = useQuery({
    queryKey: qk.admin.locations,
    queryFn: () => adminApi.listLocations(),
    staleTime: 30_000,
  });

  const resolvedName =
    currentLocationName ??
    locations.data?.find((l) => l.id === currentLocationId)?.name ??
    '(no tenant selected)';

  const switchTo = useMutation({
    mutationFn: (id: string) => adminApi.switchLocation(id),
    onSuccess: async () => {
      // Blow away everything cached from the previous tenant.
      await qc.invalidateQueries();
      setOpen(false);
    },
  });

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 rounded-md border border-mb-border bg-mb-surface px-2 py-1.5 text-left text-xs hover:border-mb-accent focus:outline-none focus:ring-2 focus:ring-mb-accent"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <ShieldCheck className="h-3.5 w-3.5 text-mb-accent-press" aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-mb-text-subtle">
            Platform admin · viewing
          </div>
          <div className="truncate font-medium text-mb-text">
            {resolvedName}
          </div>
        </div>
        <ChevronsUpDown className="h-3.5 w-3.5 text-mb-text-muted" aria-hidden />
      </button>

      {open ? (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-50 max-h-72 overflow-y-auto rounded-md border border-mb-border bg-mb-surface shadow-lg"
        >
          {locations.isLoading ? (
            <div className="p-3 text-xs text-mb-text-muted">Loading…</div>
          ) : locations.isError ? (
            <div className="p-3 text-xs text-mb-danger">Could not load tenants.</div>
          ) : locations.data && locations.data.length > 0 ? (
            locations.data.map((loc: AdminLocation) => {
              const active = loc.id === currentLocationId;
              return (
                <button
                  key={loc.id}
                  type="button"
                  onClick={() => switchTo.mutate(loc.id)}
                  disabled={switchTo.isPending}
                  className={
                    'w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-mb-accent-soft focus:outline-none ' +
                    (active ? 'bg-mb-accent-soft text-mb-accent-press' : 'text-mb-text')
                  }
                >
                  <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{loc.name}</div>
                    <div className="text-[10px] text-mb-text-subtle">
                      {loc.operator_count} ops · {loc.campaign_count} camps · {loc.lead_count} leads
                    </div>
                  </div>
                  {active ? (
                    <Check className="h-3.5 w-3.5 text-mb-accent-press" aria-hidden />
                  ) : null}
                </button>
              );
            })
          ) : (
            <div className="p-3 text-xs text-mb-text-muted">No tenants yet.</div>
          )}
        </div>
      ) : null}
    </div>
  );
}
